"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SitePlanEditor, SitePlanPrint, useSitePlan } from "@/components/tools/SitePlanCanvas";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  TextArea,
  NumberInput,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* Lift plan builder — a BASIC lift plan for routine, repetitive or low-risk
   lifting operations under LOLER 1998 reg 8 (every lifting operation must be
   planned by a competent person) and BS 7121 (Code of practice for the safe use
   of cranes / lifting operations).

   Scope guardrail (YMYL): this tool only structures a BASIC lift plan.
   Intermediate and complex lifts (BS 7121 classification) require a written plan
   prepared by a competent appointed person and are out of scope here. The tool
   never acts as the appointed person — the plan must be reviewed and authorised
   by the named appointed person before any lift.

   Domain grounding (RamsDocs knowledge bank, LOLER 1998 reg 9(3) on
   legislation.gov.uk): thorough examination by a competent person at least every
   6 months for lifting accessories (and equipment used to lift persons), and at
   least every 12 months for other lifting equipment — or in accordance with an
   examination scheme drawn up by a competent person. Source verified 2026-06-06
   via rams-knowledge against LOLER 1998 reg 9(3). */

type Classification = "basic" | "intermediate" | "complex";

const CLASSIFICATIONS: { value: Classification; label: string; note: string }[] = [
  {
    value: "basic",
    label: "Basic — routine / repetitive, low-risk lift",
    note: "Within the scope of this tool.",
  },
  {
    value: "intermediate",
    label: "Intermediate — needs more planning",
    note: "Out of scope here.",
  },
  {
    value: "complex",
    label: "Complex — tandem lift, blind lift, near services, lifting persons",
    note: "Out of scope here.",
  },
];

const MACHINE_TYPES = [
  { value: "telehandler", label: "Telehandler (lifting mode / with attachment)" },
  { value: "excavator", label: "Excavator in lifting mode (object handling)" },
  { value: "mobile-crane", label: "Mobile crane" },
  { value: "lorry-loader", label: "Lorry loader (HIAB)" },
  { value: "gantry", label: "Gantry / overhead crane" },
] as const;
type MachineType = (typeof MACHINE_TYPES)[number]["value"];

const SIGNAL_METHODS = [
  { value: "hand", label: "BS 7121 hand signals" },
  { value: "radio", label: "Two-way radio" },
  { value: "both", label: "Hand signals with radio back-up" },
] as const;
type SignalMethod = (typeof SIGNAL_METHODS)[number]["value"];

interface Accessory {
  id: number;
  type: string; // chain / round sling / shackle etc.
  swl: string; // SWL/WLL as marked
  condition: boolean; // pre-use condition check done
}

let nextAccId = 1;
function emptyAccessory(): Accessory {
  return { id: nextAccId++, type: "", swl: "", condition: false };
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatLong(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function LiftPlanBuilder() {
  const today = useMemo(() => new Date(), []);

  // Lift details
  const [liftDate, setLiftDate] = useState(toDateInput(today));
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [classification, setClassification] = useState<Classification>("basic");

  // Roles
  const [apName, setApName] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [operatorTicket, setOperatorTicket] = useState("");
  const [slingerName, setSlingerName] = useState("");
  const [slingerTicket, setSlingerTicket] = useState("");

  // Load
  const [loadDescription, setLoadDescription] = useState("");
  const [loadWeight, setLoadWeight] = useState("");
  const [weightBasis, setWeightBasis] = useState<"known" | "calculated">("known");
  const [cogKnown, setCogKnown] = useState(false);
  const [liftingPointsOk, setLiftingPointsOk] = useState(false);

  // Equipment
  const [machineType, setMachineType] = useState<MachineType>("mobile-crane");
  const [capacityAtRadius, setCapacityAtRadius] = useState("");
  const [machineTeInDate, setMachineTeInDate] = useState(false);
  const [accessoryTeInDate, setAccessoryTeInDate] = useState(false);
  const [accessories, setAccessories] = useState<Accessory[]>([emptyAccessory()]);

  // Environment
  const [groundConditions, setGroundConditions] = useState(false);
  const [overheadServices, setOverheadServices] = useState(false);
  const [windLimit, setWindLimit] = useState("");
  const [visibility, setVisibility] = useState(false);

  // Exclusion zone
  const [areaBarriered, setAreaBarriered] = useState(false);
  const [noOneUnderLoad, setNoOneUnderLoad] = useState(false);
  const [tagLines, setTagLines] = useState(false);

  // Communication
  const [signalMethod, setSignalMethod] = useState<SignalMethod>("hand");
  const [singleSignaller, setSingleSignaller] = useState(false);

  // Sequence
  const [pickUp, setPickUp] = useState("");
  const [route, setRoute] = useState("");
  const [setDown, setSetDown] = useState("");
  const [trialLift, setTrialLift] = useState(false);

  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const [sitePlan, setSitePlan] = useSitePlan("lift-plan");

  const resultTracked = useRef(false);

  const machineLabel = useMemo(
    () => MACHINE_TYPES.find((m) => m.value === machineType)?.label ?? machineType,
    [machineType],
  );
  const signalLabel = useMemo(
    () => SIGNAL_METHODS.find((s) => s.value === signalMethod)?.label ?? signalMethod,
    [signalMethod],
  );

  // Utilisation = load weight / capacity at the required radius. Flag > 75%.
  const utilisation = useMemo(() => {
    const w = parseFloat(loadWeight);
    const c = parseFloat(capacityAtRadius);
    if (!isFinite(w) || w <= 0 || !isFinite(c) || c <= 0) return null;
    const pct = (w / c) * 100;
    let level: "low" | "medium" | "high" | "veryhigh";
    let title: string;
    let advice: string;
    if (pct > 100) {
      level = "veryhigh";
      title = `${pct.toFixed(0)}% of capacity — OVER the rated capacity`;
      advice =
        "The load exceeds the machine's rated capacity at this radius. The lift must not proceed as planned — reduce the radius, use a larger machine, or split the load. Re-check the duty chart.";
    } else if (pct > 75) {
      level = "high";
      title = `${pct.toFixed(0)}% of capacity — above 75%`;
      advice =
        "Above 75% of rated capacity at this radius leaves a thin margin for the weight of slings/lifting gear, dynamic effects and any error in the load estimate. Confirm the duty chart, add the gear weight, and consider a larger machine or shorter radius. Loads over 75% are often a trigger for treating the lift as more than basic.";
    } else {
      level = "low";
      title = `${pct.toFixed(0)}% of capacity`;
      advice =
        "The load is within the rated capacity at this radius. Remember to add the weight of the slings, hook block and any lifting gear to the load before final sign-off.";
    }
    return { pct, level, title, advice };
  }, [loadWeight, capacityAtRadius]);

  const updateAcc = (id: number, patch: Partial<Accessory>) =>
    setAccessories((as) => as.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const outOfScope = classification !== "basic";

  // Fire the funnel event once per visit when the plan first has meaningful content.
  const hasContent = description.trim().length > 0 || loadDescription.trim().length > 0;
  useEffect(() => {
    if (hasContent && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("lift-plan", { classification });
    }
  }, [hasContent, classification]);

  const namedAccessories = accessories.filter((a) => a.type.trim().length > 0);

  // A lift plan the appointed person can actually review needs all of these —
  // drives the sticky progress panel and the print guard.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Location", done: location.trim().length > 0 },
    { label: "Description of the lifting operation", done: description.trim().length > 0 },
    { label: "Appointed person", done: apName.trim().length > 0 },
    { label: "Operator", done: operatorName.trim().length > 0 },
    { label: "Slinger / signaller", done: slingerName.trim().length > 0 },
    { label: "Load description", done: loadDescription.trim().length > 0 },
    { label: "Load weight", done: loadWeight.trim().length > 0 },
    { label: "Capacity at working radius", done: capacityAtRadius.trim().length > 0 },
    { label: "Machine thorough examination in date", done: machineTeInDate },
    { label: "Accessory thorough examination in date", done: accessoryTeInDate },
    { label: "Exclusion zone barriered", done: areaBarriered },
    { label: "No one under the load", done: noOneUnderLoad },
    { label: "Pick-up point", done: pickUp.trim().length > 0 },
    { label: "Set-down point", done: setDown.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="space-y-6">
      {/* Sticky progress + next gap, so the long form keeps its bearings. */}
      <div className="sticky top-20 z-10">
        <CompletionPanel items={requiredItems} title="Lift plan completion" />
      </div>

      {/* Scope intro */}
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
        <strong>Every lifting operation must be planned by a competent person (LOLER 1998 reg 8).</strong>{" "}
        This builder produces a <strong>basic lift plan</strong> for routine, low-risk lifts. The finished plan
        must be reviewed and authorised by the named appointed person before the lift — this tool does not act
        as the appointed person and does not authorise anything.
      </div>

      {/* Lift details */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Lift details</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Date of lift">
            <TextInput type="date" value={liftDate} onChange={(e) => setLiftDate(e.target.value)} />
          </Field>
          <Field label="Location">
            <RememberedTextInput store="sites" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Plot 4, north compound" />
          </Field>
          <Field label="Description of the lifting operation" className="sm:col-span-2">
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Lift packs of blocks from delivery lorry to slab edge with the site crane."
            />
          </Field>
        </div>
      </div>

      {/* Classification */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Lift classification</h2>
        <p className="mt-1 text-sm text-muted">
          BS 7121 classifies lifts as basic, intermediate or complex. This tool covers basic lifts only.
        </p>
        <div className="mt-4">
          <Field label="Classification">
            <SelectInput
              value={classification}
              onChange={(e) => setClassification(e.target.value as Classification)}
            >
              {CLASSIFICATIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </SelectInput>
          </Field>
        </div>
        {outOfScope && (
          <div className="mt-4">
            <ResultBanner
              level="high"
              title={`An ${classification} lift is beyond a basic lift plan`}
            >
              Intermediate and complex lifts must have a <strong>written plan prepared by a competent
              appointed person</strong>, who selects the equipment, assesses the lift and supervises it. This
              tool only covers basic lifts — stop here and have the appointed person produce the full plan.
            </ResultBanner>
          </div>
        )}
      </div>

      {/* Roles */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Roles</h2>
        <p className="mt-1 text-sm text-muted">
          On a basic lift one competent person may hold more than one role — but each role must still be a
          named, competent, suitably ticketed individual.
        </p>
        <div className="mt-4 grid gap-4">
          <Field
            label="Appointed person (plans & authorises the lift)"
            hint="Names the person who plans the lift and authorises it to proceed. This is not you — name the competent appointed person."
          >
            <RememberedTextInput store="people" value={apName} onChange={(e) => setApName(e.target.value)} placeholder="Name" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Crane / plant operator">
              <RememberedTextInput store="people" value={operatorName} onChange={(e) => setOperatorName(e.target.value)} placeholder="Name" />
            </Field>
            <Field label="Operator ticket / competence" hint="e.g. CPCS / NPORS category for the machine.">
              <TextInput value={operatorTicket} onChange={(e) => setOperatorTicket(e.target.value)} placeholder="e.g. CPCS A66 / NPORS" />
            </Field>
            <Field label="Slinger / signaller">
              <RememberedTextInput store="people" value={slingerName} onChange={(e) => setSlingerName(e.target.value)} placeholder="Name" />
            </Field>
            <Field label="Slinger ticket / competence" hint="e.g. CPCS A40 / NPORS slinger-signaller.">
              <TextInput value={slingerTicket} onChange={(e) => setSlingerTicket(e.target.value)} placeholder="e.g. CPCS A40 / NPORS" />
            </Field>
          </div>
          <RememberedNamesNote stores={["people", "sites", "plant"]} />
        </div>
      </div>

      {/* Load */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Load</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Load description">
            <TextInput value={loadDescription} onChange={(e) => setLoadDescription(e.target.value)} placeholder="e.g. Pack of dense concrete blocks, banded" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Load weight (kg)">
              <NumberInput min={0} step={1} value={loadWeight} onChange={(e) => setLoadWeight(e.target.value)} placeholder="e.g. 1200" />
            </Field>
            <Field label="Weight is" hint="A calculated/estimated weight needs a margin and verification.">
              <SelectInput value={weightBasis} onChange={(e) => setWeightBasis(e.target.value as "known" | "calculated")}>
                <option value="known">Known (marked / weighed / from data)</option>
                <option value="calculated">Calculated / estimated</option>
              </SelectInput>
            </Field>
          </div>
          <CheckboxRow checked={cogKnown} onChange={setCogKnown}>
            Centre of gravity is known and the load will hang level / be slung accordingly
          </CheckboxRow>
          <CheckboxRow checked={liftingPointsOk} onChange={setLiftingPointsOk}>
            Lifting points / slinging method are adequate and rated for the load
          </CheckboxRow>
        </div>
      </div>

      {/* Equipment */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Equipment</h2>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Machine type">
              <SelectInput value={machineType} onChange={(e) => setMachineType(e.target.value as MachineType)}>
                {MACHINE_TYPES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Rated capacity at the required radius (kg)" hint="From the machine's duty chart for the actual radius and configuration.">
              <NumberInput min={0} step={1} value={capacityAtRadius} onChange={(e) => setCapacityAtRadius(e.target.value)} placeholder="e.g. 2000" />
            </Field>
          </div>

          {utilisation && (
            <ResultBanner level={utilisation.level} title={`Utilisation: ${utilisation.title}`}>
              {utilisation.advice}
            </ResultBanner>
          )}

          <div className="rounded-lg border border-line bg-mist/60 p-4 space-y-2">
            <div className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">
              LOLER thorough examination in date
            </div>
            <CheckboxRow checked={machineTeInDate} onChange={setMachineTeInDate}>
              Machine / lifting appliance — thorough examination within the last 12 months (or per its examination scheme)
            </CheckboxRow>
            <CheckboxRow checked={accessoryTeInDate} onChange={setAccessoryTeInDate}>
              Lifting accessories (chains, slings, shackles) — thorough examination within the last 6 months (or per scheme)
            </CheckboxRow>
            <p className="text-[0.78rem] leading-relaxed text-muted">
              LOLER 1998 reg 9(3): at least every 6 months for lifting accessories (and equipment that lifts
              people), at least every 12 months for other lifting equipment, or in accordance with a written
              examination scheme drawn up by a competent person.
            </p>
          </div>

          <Field label="Lifting accessories" hint="Chains, slings, shackles etc. — check each is marked with its SWL/WLL and visually inspected before use.">
            <div className="space-y-3">
              {accessories.map((a, i) => (
                <div key={a.id} className="rounded-lg border border-line bg-white p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">Accessory {i + 1}</span>
                    {accessories.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setAccessories((as) => as.filter((x) => x.id !== a.id))}
                        className="text-[0.78rem] font-semibold text-muted hover:text-risk-veryhigh"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <RememberedTextInput store="plant" value={a.type} onChange={(e) => updateAcc(a.id, { type: e.target.value })} placeholder="e.g. 2-leg chain sling" aria-label="Accessory type" />
                    <TextInput value={a.swl} onChange={(e) => updateAcc(a.id, { swl: e.target.value })} placeholder="SWL / WLL, e.g. 3.15 t" aria-label="Safe working load" />
                  </div>
                  <div className="mt-2">
                    <CheckboxRow checked={a.condition} onChange={(v) => updateAcc(a.id, { condition: v })}>
                      Pre-use condition check done — no damage, distortion, wear or missing markings
                    </CheckboxRow>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setAccessories((as) => [...as, emptyAccessory()])}
                className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
              >
                + Add accessory
              </button>
            </div>
          </Field>
        </div>
      </div>

      {/* Environment */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Environment</h2>
        <div className="mt-4 grid gap-4">
          <CheckboxRow checked={groundConditions} onChange={setGroundConditions}>
            Ground conditions assessed — firm, level standing; outrigger / spreader mats used as needed
          </CheckboxRow>
          <CheckboxRow checked={overheadServices} onChange={setOverheadServices}>
            Overhead services / structures identified and an exclusion / no-go distance set
          </CheckboxRow>
          <Field label="Wind limit (m/s)" hint="The maximum safe wind speed from the manufacturer's data for the machine and load. Stop if exceeded.">
            <TextInput value={windLimit} onChange={(e) => setWindLimit(e.target.value)} placeholder="e.g. 9 m/s — check the duty chart" />
          </Field>
          <CheckboxRow checked={visibility} onChange={setVisibility}>
            Adequate visibility / lighting for the operator and signaller throughout the lift
          </CheckboxRow>
        </div>
      </div>

      {/* Exclusion zone */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Exclusion zone</h2>
        <div className="mt-4 grid gap-4">
          <CheckboxRow checked={areaBarriered} onChange={setAreaBarriered}>
            Lift area barriered off and the exclusion zone clear of other people
          </CheckboxRow>
          <CheckboxRow checked={noOneUnderLoad} onChange={setNoOneUnderLoad}>
            No one will pass or stand under a suspended load
          </CheckboxRow>
          <CheckboxRow checked={tagLines} onChange={setTagLines}>
            Tag lines used to control the load where it could swing or rotate
          </CheckboxRow>
        </div>
      </div>

      {/* Communication */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Communication</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Signalling method">
            <SelectInput value={signalMethod} onChange={(e) => setSignalMethod(e.target.value as SignalMethod)}>
              {SIGNAL_METHODS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </SelectInput>
          </Field>
          <CheckboxRow checked={singleSignaller} onChange={setSingleSignaller}>
            One nominated signaller directs the lift at a time (the operator stops on any STOP signal from anyone)
          </CheckboxRow>
        </div>
      </div>

      {/* Sequence */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Lift sequence</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Pick-up point">
            <TextInput value={pickUp} onChange={(e) => setPickUp(e.target.value)} placeholder="Where the load is lifted from" />
          </Field>
          <Field label="Travel route / slew">
            <TextInput value={route} onChange={(e) => setRoute(e.target.value)} placeholder="Path the load takes; areas kept clear" />
          </Field>
          <Field label="Set-down point">
            <TextInput value={setDown} onChange={(e) => setSetDown(e.target.value)} placeholder="Where the load is landed" />
          </Field>
          <CheckboxRow checked={trialLift} onChange={setTrialLift}>
            A trial lift (lift just clear of the ground, check stability and slinging) will be done before travelling
          </CheckboxRow>
        </div>
      </div>

      <SitePlanEditor
        value={sitePlan}
        onChange={setSitePlan}
        defaultPreset="lift"
        title="Lift area sketch"
        hint="Draw the exclusion zone, crane position with its radius, the pick-up and set-down points, and how people are kept out. The sketch prints with the lift plan."
      />

      <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />

      <GuardedPrintButton
        tool="lift-plan"
        label="Print / save lift plan as PDF"
        missing={missingRequired}
        warningTitle="This lift plan still has missing required fields or unconfirmed checks"
      />

      {/* Printable lift plan */}
      <div className="print-area card shadow-soft p-6" id="lift-plan-record">
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
          Basic lift plan — LOLER 1998 / BS 7121
        </div>
        <h2 className="mt-1 text-lg">
          {location || "Lift location"}
          {liftDate ? ` — ${formatLong(liftDate)}` : ""}
        </h2>

        {outOfScope && (
          <div className="mt-4">
            <ResultBanner level="high" title="This lift is not a basic lift">
              You selected an <strong>{classification}</strong> lift. This basic lift plan is not sufficient —
              a competent appointed person must produce a written plan for the lift.
            </ResultBanner>
          </div>
        )}

        <p className="mt-3 rounded-lg bg-mist px-4 py-3 text-[0.82rem] leading-relaxed text-muted">
          A basic lift plan for a routine, low-risk lift. It must be reviewed and authorised by the appointed
          person below before the lift, and re-planned if the load, equipment, location or conditions change.
        </p>

        {/* Details + classification */}
        <table className="mt-4 w-full text-sm">
          <tbody>
            <tr className="border-b border-line/60">
              <td className="w-44 py-1.5 pr-2 font-semibold text-ink">Lifting operation</td>
              <td className="py-1.5 text-body">{description || "—"}</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-1.5 pr-2 font-semibold text-ink">Classification</td>
              <td className="py-1.5 text-body capitalize">{classification}</td>
            </tr>
          </tbody>
        </table>

        {/* Roles table */}
        <h3 className="mt-5 font-display text-base font-bold text-ink">Roles</h3>
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
              <th className="py-1.5 pr-2 font-semibold">Role</th>
              <th className="py-1.5 pr-2 font-semibold">Name</th>
              <th className="py-1.5 font-semibold">Ticket / competence</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-line/60">
              <td className="py-2 pr-2 font-semibold text-ink">Appointed person</td>
              <td className="py-2 pr-2 text-body">{apName || "—"}</td>
              <td className="py-2 text-body">Plans &amp; authorises the lift</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-2 pr-2 font-semibold text-ink">Operator</td>
              <td className="py-2 pr-2 text-body">{operatorName || "—"}</td>
              <td className="py-2 text-body">{operatorTicket || "—"}</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-2 pr-2 font-semibold text-ink">Slinger / signaller</td>
              <td className="py-2 pr-2 text-body">{slingerName || "—"}</td>
              <td className="py-2 text-body">{slingerTicket || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* Load + equipment */}
        <h3 className="mt-5 font-display text-base font-bold text-ink">Load &amp; equipment</h3>
        <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[180px_1fr]">
          <dt className="font-semibold text-ink">Load</dt>
          <dd className="text-body">{loadDescription || "—"}</dd>

          <dt className="font-semibold text-ink">Weight</dt>
          <dd className="text-body">
            {loadWeight ? `${loadWeight} kg (${weightBasis})` : "—"}
          </dd>

          <dt className="font-semibold text-ink">Centre of gravity</dt>
          <dd className="text-body">{cogKnown ? "Known / accounted for" : "Not confirmed"}</dd>

          <dt className="font-semibold text-ink">Lifting points / slinging</dt>
          <dd className="text-body">{liftingPointsOk ? "Adequate and rated" : "Not confirmed"}</dd>

          <dt className="font-semibold text-ink">Machine</dt>
          <dd className="text-body">{machineLabel}</dd>

          <dt className="font-semibold text-ink">Capacity at radius</dt>
          <dd className="text-body">{capacityAtRadius ? `${capacityAtRadius} kg` : "—"}</dd>

          <dt className="font-semibold text-ink">Utilisation</dt>
          <dd className="text-body">
            {utilisation ? `${utilisation.pct.toFixed(0)}% of rated capacity${utilisation.pct > 75 ? " — review (>75%)" : ""}` : "—"}
          </dd>

          <dt className="font-semibold text-ink">Thorough examination</dt>
          <dd className="text-body">
            Machine: {machineTeInDate ? "in date (≤12 months)" : "not confirmed"}; accessories:{" "}
            {accessoryTeInDate ? "in date (≤6 months)" : "not confirmed"}
          </dd>

          <dt className="font-semibold text-ink">Accessories</dt>
          <dd className="text-body">
            {namedAccessories.length ? (
              <ul className="list-disc space-y-0.5 pl-4">
                {namedAccessories.map((a) => (
                  <li key={a.id}>
                    {a.type}
                    {a.swl ? ` — SWL ${a.swl}` : ""} ({a.condition ? "checked" : "not checked"})
                  </li>
                ))}
              </ul>
            ) : (
              "—"
            )}
          </dd>
        </dl>

        {/* Controls */}
        <h3 className="mt-5 font-display text-base font-bold text-ink">Site controls</h3>
        <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[180px_1fr]">
          <dt className="font-semibold text-ink">Ground / outriggers</dt>
          <dd className="text-body">{groundConditions ? "Assessed; mats used as needed" : "Not confirmed"}</dd>

          <dt className="font-semibold text-ink">Overhead services</dt>
          <dd className="text-body">{overheadServices ? "Identified; exclusion distance set" : "Not confirmed"}</dd>

          <dt className="font-semibold text-ink">Wind limit</dt>
          <dd className="text-body">{windLimit || "—"}</dd>

          <dt className="font-semibold text-ink">Visibility / lighting</dt>
          <dd className="text-body">{visibility ? "Adequate" : "Not confirmed"}</dd>

          <dt className="font-semibold text-ink">Exclusion zone</dt>
          <dd className="text-body">
            {[
              areaBarriered && "Area barriered",
              noOneUnderLoad && "No one under suspended load",
              tagLines && "Tag lines for control",
            ]
              .filter(Boolean)
              .join("; ") || "Not confirmed"}
          </dd>

          <dt className="font-semibold text-ink">Communication</dt>
          <dd className="text-body">
            {signalLabel}
            {singleSignaller ? "; one nominated signaller (anyone may signal STOP)" : ""}
          </dd>
        </dl>

        {/* Sequence */}
        <h3 className="mt-5 font-display text-base font-bold text-ink">Lift sequence</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-[0.88rem] text-body">
          <li>Pick up: {pickUp || "—"}</li>
          {trialLift && <li>Trial lift — raise just clear of the ground, check stability and slinging.</li>}
          <li>Travel / slew: {route || "—"}</li>
          <li>Set down: {setDown || "—"}</li>
        </ol>

        {/* Sign-off */}
        <h3 className="mt-6 font-display text-base font-bold text-ink">Sign-off</h3>
        <p className="mt-1 text-[0.82rem] text-muted">
          This plan is only authorised once the appointed person has reviewed and signed it.
        </p>
        <div className="mt-3 grid gap-6 sm:grid-cols-2">
          <div>
            <div className="text-[0.82rem] font-semibold text-ink">Appointed person (review &amp; authorise)</div>
            <div className="mt-6 border-b border-ink/60" />
            <div className="mt-1 flex justify-between text-[0.78rem] text-muted">
              <span>{apName || "Name"}</span>
              <span>Signature / date</span>
            </div>
          </div>
          <div>
            <div className="text-[0.82rem] font-semibold text-ink">Operator (accepts the plan)</div>
            <div className="mt-6 border-b border-ink/60" />
            <div className="mt-1 flex justify-between text-[0.78rem] text-muted">
              <span>{operatorName || "Name"}</span>
              <span>Signature / date</span>
            </div>
          </div>
        </div>

        <p className="mt-5 text-[0.78rem] leading-relaxed text-muted">
          This builder structures a basic lift plan only. Intermediate and complex lifts need a written plan by
          a competent appointed person. Whether this plan is suitable and sufficient — and whether the lift may
          proceed — is the appointed person&apos;s decision, not this tool&apos;s.
        </p>

        <SitePlanPrint value={sitePlan} heading="Lift area sketch — exclusion zone & crane position" />

        <EvidencePrint value={evidence} briefing />

        <PrintFooter toolName="lift plan builder" />
      </div>
    </div>
  );
}
